import { useEffect, useState } from 'react';
import api from '../services/service';
import { toast } from 'react-toastify';
import { loginSuccess, logout } from '../../redux/slices/authSlice';
import store from '../../redux/store';

export function useDebounce(value, delay, setPage) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    setPage(1);
    const handler = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(handler); // cancel previous timeout
  }, [value, delay, setPage]);

  return debounced;
}

let toastId = null;
const showToast = (message) => {
  if (!toast.isActive(toastId)) {
    toastId = toast(message);
  }
};

export const errorResponseMessage = (error) => {
  const response = error?.response;
  if (error?.code === 'ERR_NETWORK') {
    showToast('Network Error');
  } else if (response?.status === 500) {
    showToast(response?.statusText || response?.data?.message);
  } else if (response?.status === 400) {
    if (response?.data?.data || !("message" in (response?.data || {}))) {
      Object.entries(response?.data?.data || response?.data).forEach(([key, value]) => {
        toast(
          `${key
            .split('_')
            .reduce(
              (acc, word) =>
                acc + word.charAt(0).toUpperCase() + word.slice(1) + ' ',
              ''
            )
            .trim()}: ${value}`
        );
      });
    }else{
      const message = response?.data?.message;
      if (Array.isArray(message)) {
        message.forEach(msg => showToast(msg));
      } else if (message) {
        showToast(message);
      }
    }
  } else {
    showToast(response?.data?.message);
  }
};

export const getListData = async (
  endpoint,
  setLoading,
  setData,
  page = 1,
  page_size = 10,
  params = ''
) => {
  await api
    .get(`${endpoint}?page=${page}&page_size=${page_size}${params}`)
    .then(function (response) {
      setData(response?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      setLoading(false);
    });
};

export const createData = async (
  endpoint,
  setLoading,
  body,
  navigate,
  redirect
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  api
    .post(endpoint, body, { headers })
    .then(function (response) {
      // handle success
      navigate(redirect);
      showToast(response.data.message);
    })
    .catch(function (error) {
      // handle error
      errorResponseMessage(error);
    })
    .finally(function () {
      setLoading(false);
    });
};

export const getData = async (endpoint, setLoading, setData, id) => {
  await api
    .get(`${endpoint}${id}/`)
    .then(function (response) {
      setData(response?.data?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      setLoading(false);
    });
};

export const updateData = async (
  endpoint,
  id,
  setLoading,
  body,
  navigate,
  redirect
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  await api
    .put(`${endpoint}${id}/`, body, { headers })
    .then(function (response) {
      navigate(redirect);
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      setLoading(false);
    });
};


export const patchActiveData = async (
  endpoint,
  id,
  setLoading,
  body,
  navigate,
  redirect
) => {
  const headers = {};
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  await api
    .patch(`${endpoint}${id}/`, body, { headers })
    .then(function (response) {
      navigate(redirect);
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      setLoading(false);
    });
};

export const deleteData = async (endpoint, id) => {
  await api
    .delete(`${endpoint}${id}/`)
    .then(function (response) {
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    });
};

export const deleteOrderData = async (endpoint, id) => {
  await api
    .delete(`${endpoint}?order_id=${id}`)
    .then(function (response) {
      showToast(response?.data?.message);
    })
    .catch(function (error) {
      console.log(error);
      errorResponseMessage(error);
    });
};

export const getList = async (
  endpoint,
  setLoading,
  setData
) => {
  await api
    .get(`${endpoint}`)
    .then(function (response) {
      setData(response?.data?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      if (setLoading) setLoading(false);
    });
};
export const getDashboardData = async (
  endpoint,
  setLoading,
  setData
) => {
  await api
    .get(`${endpoint}`)
    .then(function (response) {
      setData(response?.data);
    })
    .catch(function (error) {
      // handle error
      console.log(error);
      errorResponseMessage(error);
    })
    .finally(function () {
      // always executed
      if (setLoading) setLoading(false);
    });
};

export const userLogin = async (body, setLoading, navigate, redirect) => {
  await api
    .post('/users/login/', body)
    .then((response) => {
      const { tokens, user } = response.data;
      store.dispatch(loginSuccess({ token: tokens, user }));
      navigate(redirect);
      showToast(response.data.message);
    })
    .catch((error) => {
      errorResponseMessage(error);
    })
    .finally(() => {
      setLoading(false);
    });
};


export const userLogout = async (navigate, redirect) => {
  const refreshToken = store.getState().auth.token?.refresh;


  if (!refreshToken) {
    store.dispatch(logout());
    navigate(redirect);
    return;
  }


  try {
    const response = await api.post('/users/logout/', { refresh: refreshToken });


    store.dispatch(logout());
    navigate(redirect);
    showToast(response?.data?.message || "Logout successful.");
  } catch (error) {
 
    store.dispatch(logout());
    navigate(redirect);
    showToast("Logged out locally due to server error.");
  }
};

// ResetPassword
export const createResetData = async (
  endpoint,
  setLoading,
  body,
  onSuccess = () => {},
  redirect = "",
  navigate = null // <- Add this
) => {
  try {
    const response = await api.post(endpoint, body);
    showToast(response.data.message);
    onSuccess();
    if (redirect && navigate) {
      navigate(redirect);
    }
  } catch (error) {
    errorResponseMessage(error);
  } finally {
    setLoading(false);
  }
};

export const downloadFile = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, {
      params,
      responseType: 'blob', // important for downloading files
    });

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    // filename from content-disposition OR fallback
    const contentDisposition = response.headers['content-disposition'];
    const fileNameMatch = contentDisposition?.match(/filename="?(.+)"?/);
    const filename = fileNameMatch ? fileNameMatch[1] : 'downloaded_file.xlsx';

    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
  }
};
export const patchData = async (
  endpoint,
  payload,
  setLoading,
) => {
  if (setLoading) setLoading(true);
  try {
    const response = await api.patch(`${endpoint}`, payload);
    return response?.data; // <-- yaha se return karo
  } catch (error) {
    console.log(error);
    errorResponseMessage(error);
    return null;
  } finally {
    if (setLoading) setLoading(false);
  }
};
